import React, { useCallback, useSyncExternalStore } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import styled from "~ui/styled"
import { Pressable } from "~ui/primitives"
import Icon from "~widgets/icon"
import { getScreenOptions, subscribeScreenOptions } from "./screen-options-store"

const Page = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.blockBackground};
`

const Header = styled.header`
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 48px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const BackButton = styled(Pressable)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeaderRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 10px;
  box-sizing: border-box;
`

const Title = styled.span`
  max-width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text};
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

function useDynamicScreenOptions() {
  const location = useLocation()
  const pathname = location.pathname
  return useSyncExternalStore(
    subscribeScreenOptions,
    () => getScreenOptions(pathname),
    () => getScreenOptions(pathname),
  )
}

export default function ScreenLayout({
  title,
  headerShown = true,
  headerBackVisible: configHeaderBackVisible,
  configHeaderRight,
  children,
}: {
  title?: string
  headerShown?: boolean
  headerBackVisible?: boolean
  configHeaderRight?: () => React.ReactNode
  children?: React.ReactNode
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const dynamicOptions = useDynamicScreenOptions()
  const showHeader = headerShown && dynamicOptions.headerShown !== false
  const showBack =
    dynamicOptions.headerBackVisible !== false &&
    configHeaderBackVisible !== false
  const headerTitle = dynamicOptions.title ?? title
  const HeaderRightRenderer = dynamicOptions.headerRight ?? configHeaderRight

  const handleBack = useCallback(() => {
    const isSecurity = (() => {
      if (location.pathname !== "/settings") return false
      const params = new URLSearchParams(location.search)
      return params.get("type") === "security"
    })()

    // 安全设置页：强制回到“我的”，不走 history back
    if (isSecurity) {
      navigate("/me", { replace: true })
      return
    }

    navigate(-1)
  }, [location.pathname, location.search, navigate])

  return (
    <Page>
      {showHeader ? (
        <Header>
          {showBack ? (
            <BackButton type="button" aria-label="返回" onClick={handleBack}>
              <Icon name="left" size={16} />
            </BackButton>
          ) : null}
          <Title>{headerTitle}</Title>
          {HeaderRightRenderer ? (
            <HeaderRight>
              <HeaderRightRenderer />
            </HeaderRight>
          ) : null}
        </Header>
      ) : null}
      <Body>{children ?? <Outlet />}</Body>
    </Page>
  )
}
